import { FoodItem, FoodItemKey } from "client/jspPlatformExperiment";
import { isFoodItem } from "models/food/item/foodItem";
import { normalizeForSearch } from "utils/common/stringUtils";

export const ingredientCategoriesOptions = [
  "other",
  "meats",
  "seafood",
  "fruits",
  "veggies",
  "grains",
  "spices",
  "dairy",
  "nuts",
  "legumes",
  "standard",
  "alcohol",
  "sides",
  "fungi",
].sort();

/**
 * Get the food item option name shown in drop-down menu most of the time
 * @param food The food item key or food item object
 */
export const getFoodItemOptionName = (food: FoodItemKey | FoodItem): string => {
  const beginningString = `${food.food_name ?? ""} - ${food.id}`;

  if (
    !isFoodItem(food) ||
    (isFoodItem(food) && !!food.food_recipes && food.food_recipes.length !== 0)
  ) {
    return beginningString;
  }
  return `${beginningString} - [ING]`;
};

/**
 * Autocomplete filter for food item option drop-downs: matches on the option
 * name ignoring case and accents, capped at 50 results so the list stays fast
 * with 1000+ options.
 * @param options The food item options offered by the autocomplete
 * @param state The autocomplete filter state carrying the typed input value
 */
export const filterFoodItemOptionsForSearch = <
  T extends FoodItemKey | FoodItem,
>(
  options: T[],
  state: { inputValue: string },
): T[] => {
  if (state.inputValue) {
    const normalizedInput = normalizeForSearch(state.inputValue);
    return options
      .filter((option) =>
        normalizeForSearch(getFoodItemOptionName(option)).includes(
          normalizedInput,
        ),
      )
      .slice(0, 50);
  }
  return options.slice(0, 50);
};

/**
 * Get the food item option maps to use in multiple components
 * @param foodItemOptions The array of food item or food item key objects
 */
export const getFoodItemOptionMaps = (
  foodItemOptions: Array<FoodItemKey | FoodItem>,
): {
  foodItemOptionNamesToIdsMap: Record<string, number>;
  idsToFoodItemOptionNamesMap: Map<number, string>;
  idsToFoodNamesMap: Map<number, string>;
} => {
  const foodItemOptionNamesToIdsMap: Record<string, number> = { "": 0 };
  const idsToFoodItemOptionNamesMap: Map<number, string> = new Map([[0, ""]]);
  const idsToFoodNamesMap: Map<number, string> = new Map([[0, ""]]);
  foodItemOptions.map((foodItemKey) => {
    const foodItemOptionName = getFoodItemOptionName(foodItemKey);
    foodItemOptionNamesToIdsMap[foodItemOptionName] = foodItemKey.id;
    idsToFoodItemOptionNamesMap.set(foodItemKey.id, foodItemOptionName);
    idsToFoodNamesMap.set(foodItemKey.id, foodItemKey.food_name ?? "");
    return foodItemOptionNamesToIdsMap;
  });
  return {
    foodItemOptionNamesToIdsMap,
    idsToFoodItemOptionNamesMap,
    idsToFoodNamesMap,
  };
};
